/**
 * Module dependencies.
 */

import { Text, baseFontSize, clampCalc } from 'src/components/core/text';
import { media } from 'src/styles/media';
import { useLocalizedRoute } from 'src/core/utils/routes';
import Button from 'src/components/core/buttons/button';
import Container from 'src/components/core/layout/container';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import styled from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Section` styled component.
 */

const Section = styled.section`
  background-color: var(--color-dark);
  color: var(--color-white);
  padding: 7.875rem 0 8.375rem;

  ${media.min.md`
    padding: 12rem 0;
  `}
`;

/**
 * `Label` styled component.
 */

const Label = styled(Text)`
  font-family: var(--font-futura-pt);
  letter-spacing: clamp(
    ${15.2 / baseFontSize}rem,
    ${clampCalc(15.2, 20)},
    ${20 / baseFontSize}rem
  );
  margin-bottom: 3.125rem;
  text-align: center;
  text-transform: uppercase;

  ${media.min.md`
    margin-bottom: 3.5rem;
  `}
`;

/**
 * `PreTitle` styled component.
 */

const PreTitle = styled(Text)`
  letter-spacing: clamp(
    ${11.25 / baseFontSize}rem,
    ${clampCalc(11.25, 20.25)},
    ${20.25 / baseFontSize}rem
  );
  margin-bottom: 0;
  text-align: center;
  text-transform: uppercase;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text)`
  letter-spacing: clamp(
    ${8.25 / baseFontSize}rem,
    ${clampCalc(8.25, 23.25)},
    ${23.25 / baseFontSize}rem
  );
  margin-bottom: 0;
  text-align: center;
  text-transform: uppercase;

  @media screen and (max-width: 374px) {
    font-size: 2.8125rem;
  }
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Text)`
  color: var(--color-yellow);
  display: block;
  margin-bottom: 8.125rem;
  position: relative;
  text-align: center;
  text-transform: none;
  top: -0.75rem;

  ${media.min.md`
    margin-bottom: 8.5rem;
    top: -2.25rem;
  `}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  display: flex;
  margin: 0 auto;

  ${media.max.ms`
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  `}
`;

/**
 * `StartTourSection` component.
 */

const StartTourSection = () => {
  const { translate } = useTranslate();
  const isMobileSm = useBreakpoint('sm', 'max');
  const routeResolver = useLocalizedRoute();

  return (
    <Section>
      <Container>
        <FadeInUpAnimation
          options={{
            distance: '100%',
            transitionTime: 1
          }}
        >
          <Label
            as={'p'}
            variant={'titleXXS'}
          >
            {translate('common:sections.startTour.label')}
          </Label>
        </FadeInUpAnimation>

        <FadeInUpAnimation
          options={{
            distance: '100%',
            transitionTime: 1
          }}
        >
          <PreTitle
            as={'p'}
            variant={'titleXS'}
          >
            {translate('common:sections.startTour.preTitle')}
          </PreTitle>
        </FadeInUpAnimation>

        <FadeInUpAnimation
          options={{
            distance: '50%',
            transitionTime: 1
          }}
        >
          <Title
            as={'h3'}
            variant={'titleL'}
          >
            {translate('common:sections.startTour.title')}

            <SubTitle
              as={'span'}
              variant={'handwrittenM'}
            >
              {translate('common:sections.startTour.subtitle')}
            </SubTitle>
          </Title>
        </FadeInUpAnimation>

        <FadeInUpAnimation
          options={{
            distance: '100%',
            transitionTime: 1
          }}
        >
          <StyledButton href={routeResolver('tour')}>
            {isMobileSm
              ? translate(`common:actions.startJourneyMobile`)
              : translate(`common:actions.startJourney`)}
          </StyledButton>
        </FadeInUpAnimation>
      </Container>
    </Section>
  );
};

/**
 * Export `StartTourSection` component.
 */

export default StartTourSection;
